import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { EmpresaDto } from '../../../../services/api/empresasApi';
import api from '../../../../utils/axios';
import Swal from 'sweetalert2';

interface EmpresaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EmpresaDto) => Promise<void>;
  title: string;
  initialData?: EmpresaDto;
  isLoading: boolean;
  isEdit?: boolean;
}

interface CompressOptions {
  maxWidth: number;  
  maxHeight: number;
  quality: number;
}
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB en bytes
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

const EmpresaModal: React.FC<EmpresaModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialData,
  isLoading,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState<EmpresaDto>({
    Nombre: '',
    NIT: '',
    Direccion: '',
    RepresentanteLegal: '',
    Logo: '',
    Activo: true,
    Eliminado: false,
  });

  const [logoPreview, setLogoPreview] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      // Si hay un logo, construir la URL completa
      if (initialData.Logo) {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4020';
        setLogoPreview(`${baseUrl}${initialData.Logo}`);
      } else {
        setLogoPreview('');
      }
    } else {
      setFormData({
        Nombre: '',
        NIT: '',
        Direccion: '',
        RepresentanteLegal: '',
        Logo: '',
        Activo: true,
        Eliminado: false,
      });
      setLogoPreview('');
    }
    setFileError('');
}, [initialData, isOpen]);

  const compressImage = (file: File, options: CompressOptions): Promise<string> => {
    const { maxWidth, maxHeight, quality } = options;
  
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calcular las dimensiones manteniendo la proporción
          let width = img.width;
          let height = img.height;
  
          if (width > height) {
            if (width > maxWidth) {
              height = height * (maxWidth / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = width * (maxHeight / height);
              height = maxHeight;
            }
          }
  
          // Crear canvas y redimensionar
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('No se pudo obtener el contexto 2d'));
            return;
          }
  
          // Dibujar imagen redimensionada
          ctx.drawImage(img, 0, 0, width, height);
  
          // Convertir a base64 con calidad reducida (0.7 de 1)
          const base64 = canvas.toDataURL('image/jpeg', quality);
          resolve(base64);
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileError('');
  
    if (file) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setFileError('El archivo debe ser una imagen JPG o PNG');
        return;
      }
  
      if (file.size > MAX_FILE_SIZE) {
        setFileError('El archivo no debe superar los 20MB');
        return;
      }
  
      try {
        const formData = new FormData();
        formData.append('logo', file);
  
        const response = await api.post('/administracion/empresas/upload-logo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Construir la URL completa
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4020'; // Ajusta el puerto según tu configuración
        const fullUrl = `${baseUrl}${response.data.url}`;
        
        setLogoPreview(fullUrl);
        setFormData(prev => ({
          ...prev,
          Logo: response.data.url // Guardamos la ruta relativa en la base de datos
        }));
      } catch (error) {
        setFileError('Error al subir la imagen');
        console.error('Error uploading image:', error);
      }
    }
};

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!fileError) {
    try {
      await onSubmit(formData);
      // Solo cerramos el modal si no hay errores
    } catch (err: any) {
      onClose(); // Cerrar el modal después de mostrar el error
      const errorMessage = err.response?.data?.message || 'Error al guardar los datos';
      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
     
    }
  }
};

  const handleRemoveLogo = () => {
    setLogoPreview('');
    setFormData(prev => ({ ...prev, Logo: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setFileError('');
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre"
            fullWidth
            required
            value={formData.Nombre}
            onChange={(e) => setFormData({ ...formData, Nombre: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="NIT"
            fullWidth
            required
            value={formData.NIT}
            onChange={(e) => setFormData({ ...formData, NIT: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Dirección"
            fullWidth
            required
            value={formData.Direccion}
            onChange={(e) => setFormData({ ...formData, Direccion: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Representante Legal"
            fullWidth
            required
            value={formData.RepresentanteLegal}
            onChange={(e) => setFormData({ ...formData, RepresentanteLegal: e.target.value })}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleLogoChange}
              style={{ display: 'none' }}
              id="logo-input"
              ref={fileInputRef}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                htmlFor="logo-input"
              >
                Subir Logo
              </Button>
              {logoPreview && (
                <Button
                  color="error"
                  onClick={handleRemoveLogo}
                  sx={{ ml: 2 }}
                >
                  Eliminar Logo
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="textSecondary" display="block">
              Formatos permitidos: JPG, PNG. Tamaño máximo: 20MB
            </Typography>
            {fileError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {fileError}
              </Alert>
            )}
            {logoPreview && (
              <Box sx={{ mt: 2, maxWidth: 200 }}>
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  style={{ width: '100%', height: 'auto' }}
                />
              </Box>
            )}
          </Box>

          {!isEdit && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.Activo}
                  onChange={(e) => setFormData({ ...formData, Activo: e.target.checked })}
                />
              }
              label="Activo"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={isLoading || !!fileError}
          >
            {isLoading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EmpresaModal;